import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { grayScale, gridCol, utilityColor } from 'components/styles';
import IconTooltip from 'components/molecules/IconTooltip';
import moment from 'moment';
import { isMacOs } from 'react-device-detect';
import StoreManagementDomain, { BudgetMonthlyItemData } from 'domain/master/storeManagement/salesBudgetYearInput';
import { BudgetType } from 'domain/master/storeManagement/salesBudgetDailyInput';
import AccordionRow from 'components/molecules/AccordionRow';
import MinusSquare from 'assets/icon/minusSquare';
import PlusSquare from 'assets/icon/plusSquare';
import {
  formatterPercent,
  showExpensesClassificationText,
} from 'components/organismos/master/storeManagement/salesBudgetYear/util';
import { SalesBudgetMonthType } from 'components/organismos/master/storeManagement/salesBudgetYear/SalesBudgetDailyInputLayout/type';
import Icon from 'components/atoms/Icon';
import { OutputType, exportCSVDailyInputFile } from 'api/salesBudgetYear';
import {
  createFileName,
  downloadCSVFile,
} from 'components/organismos/master/storeManagement/salesBudgetYear/salesBudgetYearInputLayout/hooks';
import useToastNotification from 'hooks/useToastNotification';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { Store } from 'modules/store';

const LabelRow = ({
  label,
  color,
  isExpanded,
  handleClick,
}: {
  label: string;
  color: 'lightOrange' | 'lightBlue';
  isExpanded: boolean;
  handleClick: () => void;
}) => (
  <tr onClick={handleClick}>
    <td
      style={{
        borderRight: 'none',
      }}
      className={`text-left active p-left ${color}`}
      colSpan={3}
    >
      {label}
    </td>
    <td
      className={`text-right p-left ${color}`}
      style={{
        borderLeft: 'none',
      }}
      colSpan={1}
    >
      {isExpanded ? <MinusSquare /> : <PlusSquare />}
    </td>
  </tr>
);

const ValueRow = ({ label, color }: { label: string; color: 'lightOrange' | 'lightBlue' }) => (
  <tr>
    <td
      colSpan={1}
      className={color}
      style={{
        border: '0px solid black',
        borderLeft: '1px solid black',
      }}
    />
    <td className="text-right active height_row_contents p-right" colSpan={4}>
      {label}
    </td>
  </tr>
);

const RateLabelRow = ({ label }: { label: string }) => (
  <tr>
    <td style={{ width: 31 }} className="lightYellow" />
    <td className="text-right active p-right" colSpan={3}>
      {label}
    </td>
  </tr>
);

const TotalRow = ({ color, label }: { color: 'lightOrange' | 'lightBlue'; label: string }) => (
  <tr>
    <td className={`text-center active height_row_contents ${color}`} colSpan={4}>
      {label}
    </td>
  </tr>
);

const PercentRow = ({ id, total, monthly }: { id: string; total: number; monthly: number[] }) => (
  <tr>
    <td className="text-right width_row_contents_changes" />
    <td className="text-right width_row_contents">
      <span id="costTotal">{formatterPercent.format(total ?? 0)}</span>
    </td>
    {monthly.map((item, index: number) => (
      <td key={`${id} ${index}`} className="text-right width_row_contents">
        <span>{formatterPercent.format(item ?? 0)}</span>
      </td>
    ))}
  </tr>
);

const InputCell = ({
  id,
  value,
  hasError,
  disabled,
  onChange,
}: {
  id: string;
  hasError: boolean;
  value: number | undefined;
  disabled: boolean;
  onChange: (id: string, value: number) => void;
}) => {
  const [inputValue, setInputValue] = useState(value?.toLocaleString());

  useEffect(() => setInputValue(value?.toLocaleString()), [value]);

  const handleFocus = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  return (
    <>
      {disabled ? (
        <span
          className="text-right"
          style={{
            whiteSpace: 'normal',
            display: 'inline-block',
            // maxWidth: '95px',
            overflowWrap: 'break-word',
          }}
          id={id}
        >
          {inputValue}
        </span>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <input
            className="text-right"
            style={{
              width: '85%',
              // maxWidth: '95px',
              height: '26px',
              border: hasError ? `1px solid ${utilityColor.error}` : undefined,
              marginRight: 0,
            }}
            value={inputValue}
            id={id}
            onClick={handleFocus}
            onChange={(e) => {
              let removed = Number(e.target.value.replace(/,/g, ''));

              // 数値以外が入力された場合は0にする
              if (isNaN(removed)) {
                removed = 0;
              }

              setInputValue(removed.toLocaleString());
              onChange(id, removed);
            }}
          />
        </div>
      )}
    </>
  );
};

const BudgetYearTable: React.FC<{
  getSupportDestinationOrg: string;
  selectedYear: number;
  categoryAry: string[];
  values: StoreManagementDomain;
  rows: SalesBudgetMonthType;
  formik: any;
  isReadOnly?: boolean;
  onInputChange: (field: string, value: any) => void;
}> = ({
  getSupportDestinationOrg,
  selectedYear,
  categoryAry,
  values,
  rows,
  formik,
  isReadOnly = false,
  onInputChange,
}) => {
  const {
    yearLtMonth,
    yearLtSales,
    yearLtCost,
    yearLtLabor,
    yearLtAdvertising,
    yearLtCustomPrice,
    yearLtExpenses,
    yearLtOther,
    yearLtRent,
  } = values;
  const {
    listSumOfAdvertisingSaleAndSaleBudgetFree,
    listSumOfOtherTotalCost,
    listSumOfOtherTotalCostRate,
    listSumOfEmployeeLaborCostAndPartLaborCost,
    listSumOfFoodCostAndDrinkCost,
    listSumOfAdvertisingExpenseEachDay,
    listOfOperatingIncome,
    listOfRateOperatingIncome,
    listNumberOfCustomers,
    listOfTurnoverRate,
    listOfMarginalProfit,
    listRateOfMarginalProfit,
    listOfContributionMargin,
    listOfRateContributionMargin,
    listOfBreakEventPointSale,
    listOfBreakEventPointNumber,
    listSumOfEmployeeAndPartCostRatio,
    listOfLaborShare,
    listOfFLACost,
    listOfFLACostRate,
    listOfProfitLossCost,
    listOfProfitLossCostRate,
    listOfFLAROCost,
    listOfFLAROCostRate,
    listSumOfRentEachDay,
    sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
    sumOfTotalEmployeeLaborCostAndPartLaborCost,
    sumOfAdvertisingExpense,
    sumOfLandRentRow,
    sumOfFoodCostAndDrinkCost,
    sumOfNumberOfCustomers,
    sumOfMarginalProfit,
    sumOfRateMarginalProfit,
    sumOfContributionMargin,
    sumOfRateContributionMargin,
    sumOfBreakEventPointSale,
    sumOfBreakEventPointNumber,
    sumOfSumOfEmployeeAndPartCostRatio,
    sumOfLaborShare,
    sumOfFLACostRate,
    sumOfFLACost,
    sumOfProfitLossCost,
    sumOfProfitLossCostRate,
    sumOfFLAROCost,
    sumOfOtherTotalCost,
    sumOfOtherTotalCostRate,
    totalOfOperatingIncome,
    rateOfOperationIncome,
  } = rows;

  const { errorNotification } = useToastNotification();
  const refChangeColumnWidth = useRef(Object());
  const [offsetWidthChangeColumn, setOffsetWidthChangeColumn] = useState(0);
  const [checkScrollBar, setCheckScrollBar] = useState(0);
  const salesBudgetYearLength = yearLtMonth?.length || 0;

  useEffect(() => {
    setOffsetWidthChangeColumn(refChangeColumnWidth.current?.offsetWidth || 0);
  }, [refChangeColumnWidth.current.offsetWidth]);

  useEffect(() => {
    const offsetWidth = document.getElementById('wrapTblContent')?.offsetWidth || 0;
    const clientWidth = document.getElementById('wrapTblContent')?.offsetWidth || 0;
    if (offsetWidth - clientWidth === 0) {
      setCheckScrollBar(1);
    } else {
      setCheckScrollBar(0);
    }
  }, []);

  const history = useHistory();

  const roleScreen = useSelector((state: Store) => state.auth.roleScreen);

  const staffAccess =
    roleScreen &&
    roleScreen.useStaffAccess === true &&
    roleScreen.staffAccessFunctionRoles.filter((role: any) => role.orgCode === getSupportDestinationOrg);

  const checkUserRole =
    getSupportDestinationOrg !== 'all' && staffAccess && staffAccess.length > 0 ? staffAccess[0] : roleScreen;

  // Accordion for 売上高
  const [isAdvertisingSaleDataExpanded, setIsAdvertisingSaleDataExpand] = useState(true);
  // Accordion for F（食材費）
  const [isIngredientCostExpanded, setIsIngredientCostExpand] = useState(true);
  // Accordion for L（人件費）
  const [isLaborCostExpanded, setIsLaborCostExpand] = useState(true);
  // Accordion for A（広告費）
  const [isAdvertisingCostExpanded, setIsAdvertisingCostExpand] = useState(true);
  // Accordion for R（家賃）
  const [isRentExpanded, setIsRentExpand] = useState(true);
  // Accordion for O（その他経費）
  const [isOtherExpensesExpanded, setIsOtherExpensesExpand] = useState(true);

  const formatter = new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const calcHeadRightWidth = () => {
    if (!isMacOs) return 'calc(100% - 276px)';

    return checkScrollBar ? 'calc(100% - 252px)' : 'calc(100% - 269px)';
  };

  const eventScroll = (e: { target: any }) => {
    const { target } = e;
    const contentRight = document.getElementById('contentRight');
    const contentRight2 = document.getElementById('contentRight2');
    const headRight = document.getElementById('headRight');
    const scrollBottom = document.getElementById('scrollBottom');
    if (e.target.type !== 'text') {
      if (contentRight) {
        contentRight.scrollLeft = target.scrollLeft;
      }
      if (contentRight2) {
        contentRight2.scrollLeft = target.scrollLeft;
      }
      if (headRight) {
        headRight.scrollLeft = target.scrollLeft;
      }

      if (scrollBottom) {
        scrollBottom.scrollLeft = target.scrollLeft;
      }
    }
  };

  const handleClickMonth = (e: any) => {
    sessionStorage.setItem('month_selected', e);
    return history.push('/salesBudgetDailyInput');
  };

  const orgCode = sessionStorage.getItem('orgCode_DestinationOrg');

  // export
  const handleExport = (targetMonth: string) => {
    exportCSVDailyInputFile({
      outputType: OutputType.MONTHLY,
      orgCode: getSupportDestinationOrg,
      targetMonth: targetMonth,
      categoryAry,
    })
      .then((response) => {
        const fileName = createFileName(
          getSupportDestinationOrg,
          '月間日次予算',
          moment(targetMonth).format('YYYYMM'),
          '.csv',
        );
        downloadCSVFile(response, fileName);
      })
      .catch(() => {
        errorNotification('サーバー側でエラーが発生しました。');
      });
  };

  return (
    <div className="wrap-budget">
      <div
        style={{
          display: 'flex',
          marginRight: !isMacOs && checkScrollBar ? '-5px' : '0',
        }}
      >
        <div style={{ width: '249px', borderRight: '5px solid #d2d6de' }}>
          <table
            className="table table-bordered table-striped table-condensed text-nowrap"
            style={{ width: gridCol.grid12 }}
          >
            <tbody>
              <tr style={{ textAlign: 'center' }}>
                <td className="text-center">項目</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          id="headRight"
          style={{
            width: calcHeadRightWidth(),
          }}
          onScroll={eventScroll}
        >
          <div
            style={{
              width: `${(salesBudgetYearLength + 3) * 125}px`,
              marginRight: '15px',
            }}
          >
            <table
              className="table table-bordered table-striped table-condensed text-nowrap"
              style={{
                width: `${(salesBudgetYearLength + 3) * 125}px`,
              }}
            >
              <tbody>
                <tr style={{ textAlign: 'center' }}>
                  <th className="text-center width_row_contents_changes small_width" ref={refChangeColumnWidth}>
                    固変
                  </th>
                  <th className="text-center width_row_contents">合計 </th>
                  {yearLtMonth?.map((month) => {
                    const enableClick = month && orgCode;
                    const enableClickMonth = month && orgCode !== 'all';
                    return (
                      <th
                        key={month}
                        className="text-center width_row_contents"
                        onClick={enableClickMonth ? () => handleClickMonth(month) : undefined}
                        style={
                          enableClickMonth
                            ? {
                                color: 'blue',
                                textDecoration: 'underline',
                                textDecorationColor: 'blue',
                                cursor: 'pointer',
                                position: 'relative',
                              }
                            : { position: 'relative' }
                        }
                      >
                        {moment(month, 'YYYY/MM/DD').format('M月')}
                        {enableClick && checkUserRole && checkUserRole.downloadFlag === 1 && (
                          <button
                            data-tip={`日次情報ダウンロード（${moment(month, 'YYYY/MM/DD').format('M月')}）`}
                            data-for={`downloadCSV${month}`}
                            id={`downloadCSV${month}`}
                            type="button"
                            style={{
                              position: 'absolute',
                              display: 'inline',
                              cursor: 'pointer',
                              border: 'none',
                              padding: '0 4px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: 'transparent',
                              marginLeft: '5px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExport(moment(month).format('YYYY/MM/01'));
                            }}
                          >
                            <Icon type="download" color={grayScale.gray100} />
                            <ReactTooltip
                              html={true}
                              id={`downloadCSV${month}`}
                              type="dark"
                              place="top"
                              multiline={true}
                            />
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="table-content">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: gridCol.grid12,
          }}
        >
          {/* First Table */}
          <div style={{ display: 'inline-flex', width: gridCol.grid12 }} id="wrapTblContent">
            <div
              className="contentLeft"
              style={{
                width: '249px',
                minWidth: '249px',
                borderRight: '5px solid rgb(210, 214, 222)',
              }}
            >
              <table
                className="table table-bordered table-striped  table-condensed text-nowrap"
                style={{ width: gridCol.grid12 }}
              >
                <tbody>
                  <AccordionRow
                    isExpanded={isAdvertisingSaleDataExpanded}
                    trigger={
                      <LabelRow
                        label="売上高"
                        color="lightOrange"
                        isExpanded={isAdvertisingSaleDataExpanded}
                        handleClick={() => setIsAdvertisingSaleDataExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtSales?.map((item, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightOrange"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightOrange" label="【売上高】計" />

                  {/* Start F（食材費） */}
                  <AccordionRow
                    isExpanded={isIngredientCostExpanded}
                    trigger={
                      <LabelRow
                        label="F（食材費）"
                        color="lightBlue"
                        isExpanded={isIngredientCostExpanded}
                        handleClick={() => setIsIngredientCostExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtCost?.map((item, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightBlue"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightBlue" label="【F】計" />

                  <tr>
                    <td colSpan={4} className="text-right active p-right">
                      F率
                    </td>
                  </tr>
                  {/* Start 人件費 */}

                  <AccordionRow
                    isExpanded={isLaborCostExpanded}
                    trigger={
                      <LabelRow
                        label="L（人件費）"
                        color="lightBlue"
                        isExpanded={isLaborCostExpanded}
                        handleClick={() => setIsLaborCostExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtLabor?.map((item: BudgetMonthlyItemData, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightBlue"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightBlue" label="【L】計" />

                  <tr>
                    <td className="text-right active p-right" colSpan={4}>
                      L率
                    </td>
                  </tr>
                  {/* End 人件費 */}
                  {/* Start 広告宣伝費 */}

                  <AccordionRow
                    isExpanded={isAdvertisingCostExpanded}
                    trigger={
                      <LabelRow
                        label="A（広告費）"
                        color="lightBlue"
                        isExpanded={isAdvertisingCostExpanded}
                        handleClick={() => setIsAdvertisingCostExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtAdvertising?.map((item, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightBlue"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightBlue" label="【A】計" />

                  <tr>
                    <td className="text-right active p-right" colSpan={4}>
                      A率
                    </td>
                  </tr>
                  {/* End 広告宣伝費 */}
                  {/* Start FLA合計*/}
                  <tr>
                    <td colSpan={4} className="text-left p-left lightBlue">
                      FLA合計
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="text-right p-right">
                      FLA率
                    </td>
                  </tr>
                  {/* End FLA合計 */}
                  {/* Start 店舗利益 */}
                  <tr>
                    <td colSpan={4} className="text-left p-left lightYellow">
                      店舗損益
                    </td>
                  </tr>

                  <RateLabelRow label="店舗損益率" />

                  {/* End 店舗利益 */}

                  {/* Start R（家賃） */}
                  <AccordionRow
                    isExpanded={isRentExpanded}
                    trigger={
                      <LabelRow
                        label="R（家賃）"
                        color="lightBlue"
                        isExpanded={isRentExpanded}
                        handleClick={() => setIsRentExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtRent?.map((item, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightBlue"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightBlue" label="【R】計" />

                  <tr>
                    <td className="text-right active p-right" colSpan={4}>
                      R率
                    </td>
                  </tr>
                  {/* End R（家賃） */}

                  {/* Start O（その他経費）*/}

                  <AccordionRow
                    isExpanded={isOtherExpensesExpanded}
                    trigger={
                      <LabelRow
                        label="O（その他経費）"
                        color="lightBlue"
                        isExpanded={isOtherExpensesExpanded}
                        handleClick={() => setIsOtherExpensesExpand((prev) => !prev)}
                      />
                    }
                  >
                    {yearLtExpenses?.map((item, index) => (
                      <ValueRow
                        key={`item.analyticalItemCode_${index}`}
                        label={item.analyticalItemName}
                        color="lightBlue"
                      />
                    ))}
                  </AccordionRow>

                  <TotalRow color="lightBlue" label="【O】計" />

                  <tr>
                    <td className="text-right active height_row_contents p-right" colSpan={4}>
                      O率
                    </td>
                  </tr>
                  {/* End O（その他経費） */}
                  {/* Start NT（集計対象外項目） */}
                  <tr>
                    <td className="text-center active" colSpan={4} style={{ backgroundColor: '#cfe2f3' }}>
                      集計対象外項目
                    </td>
                  </tr>
                  {yearLtOther?.map((item) => (
                    <tr key={item.analyticalItemCode}>
                      <td
                        className="lightBlue"
                        style={{
                          minWidth: '7.5%',
                          width: '7.5%',
                          border: '0px solid black',
                          borderLeft: '1px solid black',
                        }}
                      />
                      <td className="text-right active height_row_contents p-right" colSpan={4}>
                        {item.analyticalItemName}
                      </td>
                    </tr>
                  ))}
                  {/* End NT（集計対象外項目） */}
                  {/* Start 【FLARO】合計 */}
                  <tr>
                    <td className="text-left active p-left lightPurple" colSpan={4}>
                      【FLARO】合計
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right active lightPurple p-right" colSpan={4}>
                      FLARO率
                    </td>
                  </tr>
                  {/* End 【FLARO】合計 */}
                  {/* Start 営業利益 */}
                  <tr>
                    <td className="text-left active p-left lightYellow" colSpan={4}>
                      営業利益
                    </td>
                  </tr>
                  <RateLabelRow label="営業利益率" />

                  {/* End 営業利益 */}
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        border: 0,
                      }}
                    />
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              id="contentRight"
              className="tabLeft"
              style={{ width: 'calc(100% - 251px)', overflowX: 'auto' }}
              onScroll={eventScroll}
            >
              <div
                style={{
                  width: `${(salesBudgetYearLength + 3) * 125}px`,
                  marginRight: '15px',
                }}
              >
                <table
                  className="table table-bordered table-striped table-condensed text-nowrap"
                  style={{ width: `${(salesBudgetYearLength + 3) * 125}px` }}
                >
                  <tbody>
                    {/* Start 売上高 */}

                    <AccordionRow
                      isExpanded={isAdvertisingSaleDataExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents">
                            <span id="salesBudgetTotal" />
                          </td>
                          {yearLtMonth?.map((_, index) => (
                            <td className="text-right width_row_contents">
                              <span id={`salesBudgetTotal${index}`} />
                            </td>
                          ))}
                        </tr>
                      }
                    >
                      {yearLtSales?.map((element, numberElement) => (
                        <tr key={`yearLtSales_${numberElement}`}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry?.map((item, monthIndex) => (
                            <td
                              key={`yearLtSales_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtSales[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedSales = [...(yearLtSales || [])];
                                  const newMonthAry = [...updatedSales[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedSales[numberElement] = {
                                    ...updatedSales[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtSales', updatedSales);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#fce5cd' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#fce5cd' }}>
                        <span id="salesBudgetTotal">
                          {sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow.toLocaleString()}
                        </span>
                      </td>
                      {listSumOfAdvertisingSaleAndSaleBudgetFree.map(
                        (item, index) =>
                          item !== null && (
                            <td className="text-right width_row_contents" style={{ backgroundColor: '#fce5cd' }}>
                              <span id={`salesBudgetTotal${index}`}>{formatter.format(item)}</span>
                            </td>
                          ),
                      )}
                    </tr>
                    {/* End 売上高 */}

                    <AccordionRow
                      isExpanded={isIngredientCostExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents" />
                          {yearLtMonth?.map((_, index) => (
                            <td className="text-right width_row_contents" />
                          ))}
                        </tr>
                      }
                    >
                      {yearLtCost?.map((element, numberElement) => (
                        <tr key={element.analyticalItemId}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry?.map((item: number, monthIndex) => (
                            <td
                              key={`yearLtCost_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtCost[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedCost = [...(yearLtCost || [])];
                                  const newMonthAry = [...updatedCost[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedCost[numberElement] = {
                                    ...updatedCost[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtCost', updatedCost);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    {/* Start 標準原価 */}

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="costTotal">{sumOfFoodCostAndDrinkCost.toLocaleString()}</span>
                      </td>
                      {listSumOfFoodCostAndDrinkCost.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`costTotal${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="costTotal"
                      total={
                        sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                          ? sumOfFoodCostAndDrinkCost / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                          : 0
                      }
                      monthly={listSumOfFoodCostAndDrinkCost.map((value, index) =>
                        listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          ? value / listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          : 0,
                      )}
                    />

                    {/* End 標準原価 */}
                    {/* Start L（人件費） */}

                    <AccordionRow
                      isExpanded={isLaborCostExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents" />
                          {yearLtMonth?.map((_, index) => (
                            <td className="text-right width_row_contents" />
                          ))}
                        </tr>
                      }
                    >
                      {yearLtLabor?.map((element, numberElement) => (
                        <tr key={`yearLtLabor_${numberElement}`}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry.map((item, monthIndex) => (
                            <td
                              key={`yearLtLabor_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtLabor[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedLabor = [...(yearLtLabor || [])];
                                  const newMonthAry = [...updatedLabor[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedLabor[numberElement] = {
                                    ...updatedLabor[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtLabor', updatedLabor);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="laborCostTotal">{sumOfTotalEmployeeLaborCostAndPartLaborCost.toLocaleString()}</span>
                      </td>
                      {listSumOfEmployeeLaborCostAndPartLaborCost.map((item: any, index: number) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`laborCostTotal${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="laborCostTotal"
                      total={
                        sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                          ? sumOfTotalEmployeeLaborCostAndPartLaborCost / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                          : 0
                      }
                      monthly={listSumOfEmployeeLaborCostAndPartLaborCost.map((value, index) =>
                        listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          ? value / listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          : 0,
                      )}
                    />

                    {/* End L（人件費） */}
                    {/* Start A（広告費） */}

                    <AccordionRow
                      isExpanded={isAdvertisingCostExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents" />
                          {yearLtMonth?.map((_, index) => (
                            <td key={`advertising_blank_row${index}`} className="text-right width_row_contents" />
                          ))}
                        </tr>
                      }
                    >
                      {yearLtAdvertising?.map((element, numberElement) => (
                        <tr key={`yearLtAdvertising_${numberElement}`}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry?.map((item, monthIndex) => (
                            <td
                              key={`yearLtAdvertising_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtAdvertising[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedAdvertising = [...(yearLtAdvertising || [])];
                                  const newMonthAry = [...updatedAdvertising[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedAdvertising[numberElement] = {
                                    ...updatedAdvertising[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtAdvertising', updatedAdvertising);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="laborCostTotal">{sumOfAdvertisingExpense.toLocaleString()}</span>
                      </td>
                      {listSumOfAdvertisingExpenseEachDay.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`listAdvertisingExpenseTotal${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="advertisingExpense"
                      total={
                        sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                          ? sumOfAdvertisingExpense / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                          : 0
                      }
                      monthly={listSumOfAdvertisingExpenseEachDay.map((value, index) =>
                        listSumOfAdvertisingSaleAndSaleBudgetFree[index] > 0
                          ? value / listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          : 0,
                      )}
                    />

                    {/* End A（広告費） */}
                    {/* Start FLA合計 */}
                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="advertisingMediaExpense">{sumOfFLACost.toLocaleString()}</span>
                      </td>
                      {listOfFLACost.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`costFLA${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow id="rateFLA" total={sumOfFLACostRate} monthly={listOfFLACostRate} />

                    {/* End FLA合計 */}

                    {/* Start 店舗利益*/}
                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#fff2cc' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#fff2cc' }}>
                        <span id="advertisingMediaExpense">{sumOfProfitLossCost.toLocaleString()}</span>
                      </td>
                      {listOfProfitLossCost.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#fff2cc' }}>
                          <span id={`advertisingMediaExpense${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="ProfitLossCostRate"
                      total={sumOfProfitLossCostRate}
                      monthly={listOfProfitLossCostRate}
                    />

                    {/* End 店舗利益 */}

                    {/* Start R（家賃） */}
                    <AccordionRow
                      isExpanded={isRentExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents" />
                          {yearLtMonth?.map((_, index) => (
                            <td key={`rent_blank_row${index}`} className="text-right width_row_contents" />
                          ))}
                        </tr>
                      }
                    >
                      {yearLtRent?.map((element, numberElement) => (
                        <tr key={`yearLtRent_${numberElement}`}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry?.map((item: number, monthIndex) => (
                            <td
                              key={`yearLtRent_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtRent[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedRent = [...(yearLtRent || [])];
                                  const newMonthAry = [...updatedRent[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedRent[numberElement] = {
                                    ...updatedRent[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtRent', updatedRent);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="laborCostTotal">{sumOfLandRentRow.toLocaleString()}</span>
                      </td>
                      {listSumOfRentEachDay.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`listAdvertisingExpenseTotal${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="landRentTotal"
                      total={
                        sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                          ? sumOfLandRentRow / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                          : 0
                      }
                      monthly={listSumOfRentEachDay.map((value, index) =>
                        listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          ? value / listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                          : 0,
                      )}
                    />

                    {/* End R（家賃）*/}
                    {/* Start O（その他経費） */}
                    <AccordionRow
                      isExpanded={isOtherExpensesExpanded}
                      trigger={
                        <tr>
                          <td className="text-right width_row_contents_changes" />
                          <td className="text-right width_row_contents" />
                          {yearLtMonth?.map((_, index) => (
                            <td key={`expenses_blank_row${index}`} className="text-right width_row_contents" />
                          ))}
                        </tr>
                      }
                    >
                      {yearLtExpenses?.map((element, numberElement) => (
                        <tr key={element.analyticalItemId}>
                          <td className="text-center width_row_contents_changes small_width">
                            {showExpensesClassificationText(element.expensesClassification)}
                          </td>
                          <td className="text-right width_row_contents">
                            <span>{element.monthTotal?.toLocaleString() || 0}</span>
                          </td>
                          {element.monthAry?.map((item: number, monthIndex) => (
                            <td
                              key={`yearLtExpenses_${numberElement}_${monthIndex}`}
                              className="text-right width_row_contents"
                            >
                              <InputCell
                                id={`yearLtExpenses[${numberElement}].monthAry[${monthIndex}]`}
                                value={item}
                                disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                                hasError={false}
                                onChange={(id, value) => {
                                  const updatedExpenses = [...(yearLtExpenses || [])];
                                  const newMonthAry = [...updatedExpenses[numberElement].monthAry];
                                  newMonthAry[monthIndex] = value;
                                  updatedExpenses[numberElement] = {
                                    ...updatedExpenses[numberElement],
                                    monthAry: newMonthAry,
                                    // 月次の値が変更されたら常に合計を更新
                                    monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                  };
                                  onInputChange('yearLtExpenses', updatedExpenses);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </AccordionRow>

                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#cfe2f3' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                        <span id="listExpensesTotal">{sumOfOtherTotalCost.toLocaleString()}</span>
                      </td>
                      {listSumOfOtherTotalCost.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#cfe2f3' }}>
                          <span id={`listExpensesTotal${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="listExpensesTotal"
                      total={sumOfOtherTotalCostRate}
                      monthly={listSumOfOtherTotalCostRate}
                    />

                    {/* End O（その他経費） */}
                    {/* Start 集計対象外項目 */}
                    <tr>
                      <td className="text-right width_row_contents_changes" />
                      <td className="text-right width_row_contents" />
                      {yearLtMonth?.map((_) => (
                        <td className="text-right width_row_contents" />
                      ))}
                    </tr>
                    {yearLtOther?.map((element, numberElement) => (
                      <tr key={`yearLtOther_${numberElement}`}>
                        <td className="text-center width_row_contents_changes small_width">
                          {showExpensesClassificationText(element.expensesClassification)}
                        </td>
                        <td className="text-right width_row_contents">
                          <span>{element.monthTotal?.toLocaleString() || 0}</span>
                        </td>
                        {element.monthAry.map((item, monthIndex) => (
                          <td
                            key={`yearLtOther_${numberElement}_${monthIndex}`}
                            className="text-right width_row_contents"
                          >
                            <InputCell
                              id={`yearLtOther[${numberElement}].monthAry[${monthIndex}]`}
                              value={item}
                              disabled={isReadOnly || element.budgetType !== BudgetType.YEARLY_INPUT}
                              hasError={false}
                              onChange={(id, value) => {
                                const updatedOther = [...(yearLtOther || [])];
                                const newMonthAry = [...updatedOther[numberElement].monthAry];
                                newMonthAry[monthIndex] = value;
                                updatedOther[numberElement] = {
                                  ...updatedOther[numberElement],
                                  monthAry: newMonthAry,
                                  // 月次の値が変更されたら常に合計を更新
                                  monthTotal: newMonthAry.reduce((sum, val) => sum + val, 0),
                                };
                                onInputChange('yearLtOther', updatedOther);
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* End 集計対象外項目 */}
                    {/* Start FLARO合計 */}
                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#c9daf8' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#c9daf8' }}>
                        <span id="advertisingMediaExpense">{sumOfFLAROCost.toLocaleString()}</span>
                      </td>
                      {listOfFLAROCost.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#c9daf8' }}>
                          <span id={`costFLARO${index}`}>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>

                    <PercentRow
                      id="rateFLARO"
                      total={
                        sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                          ? sumOfFLAROCost / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                          : 0
                      }
                      monthly={listOfFLAROCostRate}
                    />

                    {/* End FLARO合計 */}

                    {/* Start 店舗利益*/}
                    <tr>
                      <td className="text-right width_row_contents_changes" style={{ backgroundColor: '#fff2cc' }} />
                      <td className="text-right width_row_contents" style={{ backgroundColor: '#fff2cc' }}>
                        <span id="advertisingMediaExpense">{totalOfOperatingIncome.toLocaleString()}</span>
                      </td>
                      {listOfOperatingIncome.map((item, index) => (
                        <td className="text-right width_row_contents" style={{ backgroundColor: '#fff2cc' }}>
                          <span id={`advertisingMediaExpense${index}`}>{formatter.format(item)}</span>
                        </td>
                      ))}
                    </tr>

                    {/* Rate 店舗損益率 */}
                    <PercentRow
                      id="rateOperatingIncome"
                      total={rateOfOperationIncome}
                      monthly={listOfRateOperatingIncome}
                    />

                    {/* End 店舗利益 */}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* First Table */}
          {/* Second Table */}
          <div style={{ display: 'inline-flex', width: gridCol.grid12 }}>
            <div className="tabLeft" style={{ borderRight: '5px solid rgb(210, 214, 222)' }}>
              <div>
                <table
                  className="table table-bordered table-striped  table-condensed text-nowrap"
                  style={{ width: `${249}px` }}
                  id="salesBudgetYear-Left2"
                >
                  <tbody>
                    <tr>
                      <td className="text-right active " colSpan={3} style={{ paddingRight: 10 }}>
                        客単価
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          客数&nbsp;
                          <IconTooltip iconType="help" text="売上高合計 / 客単価" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          回転率&nbsp;
                          <IconTooltip iconType="help" text="客数 / 座席数" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center active" rowSpan={2}>
                        限界利益
                      </td>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          額&nbsp;
                          <IconTooltip iconType="help" text="売上高合計 - 変動費" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          率&nbsp;
                          <IconTooltip iconType="help" text="限界利益 / 売上高合計の％表示" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center active" rowSpan={2}>
                        貢献利益
                      </td>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          額&nbsp;
                          <IconTooltip iconType="help" text="限界利益 - 固定費" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          率&nbsp;
                          <IconTooltip iconType="help" text="貢献利益 / 売上高合計の％表示" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center active" rowSpan={2}>
                        損益分岐点
                      </td>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          売上&nbsp;
                          <IconTooltip iconType="help" text="固定費 / 限界利益率" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          客数&nbsp;
                          <IconTooltip iconType="help" text="損益分岐点売上 / 客単価" color="#000" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center active" rowSpan={yearLtLabor ? yearLtLabor.length + 1 : 1}>
                        人件費率
                      </td>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          合計&nbsp;
                          <IconTooltip iconType="help" text="人件費（合計）/ 売上高合計の％表示" color="#000" />
                        </div>
                      </td>
                    </tr>
                    {yearLtLabor?.map((item) => (
                      <tr>
                        <td className="text-right active" colSpan={4}>
                          <div className="icon-tooltip-right">
                            {item.analyticalItemName}
                            &nbsp;
                            <IconTooltip
                              iconType="help"
                              text={`${item.analyticalItemName}/ 売上高合計の％表示`}
                              color="#000"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="text-right active" colSpan={4}>
                        <div className="icon-tooltip-right">
                          労働分配率&nbsp;
                          <IconTooltip
                            iconType="help"
                            text="人件費合計 /（売上高合計 - 食材費合計）の％表示"
                            color="#000"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div
              id="contentRight2"
              className="tabLeftBottom hide_scrollbar_onscroll"
              style={{
                width: `calc(100% + ${249 + offsetWidthChangeColumn}px`,
                overflowX: 'auto',
              }}
              onScroll={eventScroll}
            >
              <div
                style={{
                  width: `${(salesBudgetYearLength + 3) * 125}px`,
                  marginRight: '15px',
                  display: 'flex',
                }}
              >
                {/* Table bottom Right */}
                <table
                  className="table table-bordered table-striped table-condensed text-nowrap"
                  style={{
                    width: `${(salesBudgetYearLength + 3) * 125 - 3}px`,
                    // width: `${1700-25}px`
                  }}
                >
                  <tbody>
                    {/* No accordion below */}
                    {/* totalPerCustomerPrice 客単価 */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span id="perCustomerPrice">
                          {(() => {
                            // 年間の客単価は売上合計÷客数合計（旧版と同じ計算方法・端数は四捨五入）
                            return sumOfNumberOfCustomers > 0
                              ? Math.round(sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow / sumOfNumberOfCustomers).toLocaleString()
                              : '0';
                          })()}
                        </span>
                      </td>
                      {yearLtCustomPrice?.monthAry?.map((item) => (
                        <td className="text-right width_row_contents">
                          <span>{item.toLocaleString()}</span>
                        </td>
                      ))}
                    </tr>
                    {/* getGuestCntTotal客数  */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span id="perCustomerPrice">{formatter.format(sumOfNumberOfCustomers)}</span>
                      </td>
                      {listNumberOfCustomers.map((item, index: number) => (
                        <td className="text-right width_row_contents">
                          <span id={`listGuestCntTotalRow${index}`}>{formatter.format(item)}</span>
                        </td>
                      ))}
                    </tr>

                    {/* getTurnoverRateTotalItems 回転率 */}
                    <tr>
                      <td className="text-right width_row_contents_changes" />
                      <td className="text-right width_row_contents">
                        <span id="costTotal">-</span>
                      </td>
                      {listOfTurnoverRate.map((item, index: number) => (
                        <td key={`listTurnoverRateTotalRow ${index}`} className="text-right width_row_contents">
                          <span>{formatterPercent.format(item ?? 0)}</span>
                        </td>
                      ))}
                    </tr>

                    {/* getTotalAllEvenMarginalProfit 限界利益額  */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span>{sumOfMarginalProfit.toLocaleString()}</span>
                      </td>
                      {listOfMarginalProfit.map((item, index) => (
                        <td className="text-right width_row_contents">
                          <span id={`listEvenMarginalProfitTotalRow${index}`}>{formatter.format(item || 0)}</span>
                        </td>
                      ))}
                    </tr>

                    {/* marginalProfitRate 限界利益率  */}
                    <PercentRow
                      id="listMarginalProfitRateRow"
                      total={sumOfRateMarginalProfit}
                      monthly={listRateOfMarginalProfit}
                    />

                    {/* contributionMarginTotal 貢献利益額 */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span id="perCustomerPrice">{sumOfContributionMargin.toLocaleString()}</span>
                      </td>
                      {listOfContributionMargin.map((item, index) => (
                        <td className="text-right width_row_contents">
                          <span id={`listContributionMarginTotalRow${index}`}>{formatter.format(item)}</span>
                        </td>
                      ))}
                    </tr>
                    {/* contributionMarginTotalRate 貢献利益率  */}
                    <PercentRow
                      id="listContributionMarginTotalRateRow"
                      total={sumOfRateContributionMargin}
                      monthly={listOfRateContributionMargin}
                    />

                    {/* 損益分岐点売上 bePointSales */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span id="perCustomerPrice">{sumOfBreakEventPointSale.toLocaleString()}</span>
                      </td>
                      {listOfBreakEventPointSale.map((item, index) => (
                        <td className="text-right width_row_contents">
                          <span id={`listBePointSalesRow${index}`}>{formatter.format(item)}</span>
                        </td>
                      ))}
                    </tr>
                    {/* 損益分岐点客数 bePointGuestCnt */}
                    <tr>
                      <td colSpan={1} className="width_row_contents_changes small_width" />
                      <td className="text-right width_row_contents_bottom">
                        <span id="perCustomerPrice">{sumOfBreakEventPointNumber.toLocaleString()}</span>
                      </td>
                      {listOfBreakEventPointNumber.map((item, index) => (
                        <td className="text-right width_row_contents">
                          <span id={`listBePointGuestCntRow${index}`}>{formatter.format(item)}</span>
                        </td>
                      ))}
                    </tr>
                    {/* 人件費率 getTotalPersonRateItems合計  */}
                    <PercentRow
                      id="listContributionMarginTotalRateRow"
                      total={sumOfSumOfEmployeeAndPartCostRatio}
                      monthly={listSumOfEmployeeAndPartCostRatio}
                    />

                    {/* 人件費率 getEmployeePersonRate 社員   */}
                    {yearLtLabor?.map((element) => (
                      <PercentRow
                        id="laborCostRate"
                        total={
                          sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow > 0
                            ? element.monthTotal / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
                            : 0
                        }
                        monthly={element.monthAry.map((item, index) =>
                          listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                            ? item / listSumOfAdvertisingSaleAndSaleBudgetFree[index]
                            : 0,
                        )}
                      />
                    ))}

                    {/* laborShare 労働分配率  */}
                    <PercentRow id="laborShareRate" total={sumOfLaborShare} monthly={listOfLaborShare} />
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Second Table */}
        </div>
      </div>

      <div className="scroll-bottom" onScroll={eventScroll}>
        <div style={{ width: '250px', float: 'left' }} />
        <div
          style={{
            width: 'calc(100% - 260px)',
            float: 'right',
            height: '24px',
            overflow: 'auto',
          }}
          id="scrollBottom"
        >
          <div className="scroll" style={{ width: `${(salesBudgetYearLength + 3) * 125}px` }} />
        </div>
      </div>
    </div>
  );
};
export default BudgetYearTable;
